import React, { useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Play, ArrowRight, MessageCircle, BarChart3, Users, Zap, QrCode, Phone } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const HeroSection = () => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  const scrollToHowItWorks = () => {
    const howItWorksSection = document.getElementById('how-it-works');
    if (howItWorksSection) {
      howItWorksSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div ref={containerRef} className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Animated Background */}
      <motion.div 
        style={{ y: backgroundY }}
        className="absolute inset-0 opacity-30"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 via-blue-600/20 to-purple-600/20 animate-pulse" />
        <div className="absolute top-1/4 left-1/4 w-48 h-48 sm:w-96 sm:h-96 bg-purple-500/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-1/4 right-1/4 w-40 h-40 sm:w-80 sm:h-80 bg-blue-500/10 rounded-full blur-3xl animate-float-delayed" />
      </motion.div>

      {/* Main Content */}
      <div className="relative z-10 flex items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            
            {/* Left Content */}
            <motion.div 
              style={{ y: textY }}
              className="text-center lg:text-left space-y-6 sm:space-y-8 order-2 lg:order-1"
            >
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center px-3 py-2 sm:px-4 sm:py-2 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs sm:text-sm font-medium"
              >
                <Zap className="w-3 h-3 sm:w-4 sm:h-4 mr-2 rtl:ml-2 rtl:mr-0" />
                {t('hero.badge')}
              </motion.div>

              {/* Main Headline */}
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight"
              >
                <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                  {t('hero.title1')}
                </span>
                <br />
                <span className="text-white">
                  {t('hero.title2')}
                </span>
                <br />
                {/* <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  Stronger Sales.
                </span> */}
              </motion.h1>

              {/* Subtext */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-base sm:text-lg lg:text-xl text-gray-300 leading-relaxed max-w-2xl mx-auto lg:mx-0"
              >
                {t('hero.description')}
              </motion.p>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="flex justify-center lg:justify-start"
              >
                <motion.button
                  onClick={() => {
                    const howItWorksSection = document.getElementById('how-it-works');
                    if (howItWorksSection) {
                      howItWorksSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="group px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300 flex items-center justify-center text-base"
                >
                  <Play className="mr-2 rtl:ml-2 rtl:mr-0 w-5 h-5 group-hover:scale-110 transition-transform" />
                  {t('hero.cta')}
                </motion.button>
              </motion.div>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="flex flex-wrap gap-4 sm:gap-8 justify-center lg:justify-start pt-6 sm:pt-8"
              >
                <div className="text-center">
                  <div className="text-xl sm:text-2xl font-bold text-white">{t('hero.stats.availability')}</div>
                  <div className="text-xs sm:text-sm text-gray-400">{t('hero.stats.availabilityLabel')}</div>
                </div>
                <div className="text-center">
                  <div className="text-xl sm:text-2xl font-bold text-white">{t('hero.stats.accuracy')}</div>
                  <div className="text-xs sm:text-sm text-gray-400">{t('hero.stats.accuracyLabel')}</div>
                </div>
                <div className="text-center">
                  <div className="text-xl sm:text-2xl font-bold text-white">{t('hero.stats.speed')}</div>
                  <div className="text-xs sm:text-sm text-gray-400">{t('hero.stats.speedLabel')}</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Right Content - Product Preview */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.4 }}
              className="relative order-1 lg:order-2"
            >
              <div className="relative max-w-sm sm:max-w-md lg:max-w-lg mx-auto">
                {/* Phone Mockup */}
                <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl sm:rounded-3xl p-1.5 sm:p-2 shadow-2xl">
                  <div className="bg-black rounded-xl sm:rounded-2xl overflow-hidden">
                    {/* Phone Screen */}
                    <div className="relative h-64 sm:h-80 lg:h-96 bg-gradient-to-br from-purple-900/20 to-blue-900/20">
                      {/* Status Bar */}
                      <div className="flex justify-between items-center p-3 sm:p-4 text-white text-xs sm:text-sm">
                        <span>9:41</span>
                        <span>KonvoAI</span>
                        <div className="flex space-x-1">
                          <div className="w-1 h-1 bg-white rounded-full"></div>
                          <div className="w-1 h-1 bg-white rounded-full"></div>
                          <div className="w-1 h-1 bg-white rounded-full"></div>
                        </div>
                      </div>

                      {/* Chat Interface */}
                      <div className="px-3 sm:px-4 space-y-2 sm:space-y-3">
                        <motion.div
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 1.2 }}
                          className="bg-purple-600 text-white p-2 sm:p-3 rounded-xl sm:rounded-2xl rounded-br-md max-w-xs ml-auto rtl:mr-auto rtl:ml-0 text-xs sm:text-sm"
                        >
                          {t('hero.demoMessages.user1')}
                        </motion.div>
                        
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 1.6 }}
                          className="bg-gray-700 text-white p-2 sm:p-3 rounded-xl sm:rounded-2xl rounded-bl-md max-w-xs text-xs sm:text-sm"
                        >
                          {t('hero.demoMessages.bot1')}

                        </motion.div>

                        <motion.div
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 2.0 }}
                          className="bg-purple-600 text-white p-2 sm:p-3 rounded-xl sm:rounded-2xl rounded-br-md max-w-xs ml-auto rtl:mr-auto rtl:ml-0 text-xs sm:text-sm"
                        >
                          {t('hero.demoMessages.user2')}
                        </motion.div>
                      </div>

                      {/* QR Code */}
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 2.4 }}
                        className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4 bg-white p-1.5 sm:p-2 rounded-lg"
                      >
                        <QrCode className="w-6 h-6 sm:w-8 sm:h-8 text-black" />
                      </motion.div>
                    </div>
                  </div>
                </div>

                {/* Floating Elements */}
                <motion.div
                  animate={{ y: [-10, 10, -10] }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className="absolute -top-2 -left-2 sm:-top-4 sm:-left-4 bg-purple-500 p-2 sm:p-3 rounded-full shadow-lg"
                >
                  <MessageCircle className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
                </motion.div>

                <motion.div
                  animate={{ y: [10, -10, 10] }}
                  transition={{ duration: 3, repeat: Infinity, delay: 1 }}
                  className="absolute -bottom-2 -right-2 sm:-bottom-4 sm:-right-4 bg-blue-500 p-2 sm:p-3 rounded-full shadow-lg"
                >
                  <BarChart3 className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;